// src/modules/app-views/suppliers/config.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************

import SuppliersInit from './pages/Init';
import ListProvider from 'components/core/ListProvider';
import { API } from 'constants/Api';
import suppliersInitActionButton from 'modules/app-views/suppliers/pages/Init/actions/actionButton';
import suppliersInitMenuButton from 'modules/app-views/suppliers/pages/Init/actions/menuButton';
import DocumentProvider from 'components/core/DocumentProvider';
import SuppliersParametersModal from 'modules/app-views/suppliers/pages/Init/modals/SuppliersParametersModal';
import seedSuppliersParameters from 'modules/app-views/suppliers/seed';
import CardHeaderTitle from 'layouts/Pages/PageHeader/CardHeaderTitle';
import CardHeaderSubtitle from 'layouts/Pages/PageHeader/CardHeaderSubtitle';
import CardHeaderCountryIcon from 'layouts/Pages/PageHeader/CardHeaderCountryIcon';
import SuppliersDocument from 'modules/app-views/suppliers/pages/Document';
import suppliersDocumentActionButton from 'modules/app-views/suppliers/pages/Document/actions/actionButton';
import suppliersDocumentDropdownMenu from 'modules/app-views/suppliers/pages/Document/actions/dropdownMenu';
import suppliersDocumentMenuButton from 'modules/app-views/suppliers/pages/Document/actions/menuButton';
import suppliersDocumentDecorateRequest from './pages/Document/decorators/request';
import CardHeaderColumn from 'layouts/Pages/PageHeader/CardHeaderColumn';
import {
  documentCodeItem,
  createdAtItem,
  updatedAtItem,
  emailItem,
  phoneItem,
  locationItem,
  websiteItem,
  genderSegmentItem,
  cpfCnpjItem,
} from 'layouts/Pages/PageHeader/CardHeaderColumn/items';
import cpfCnpjInput from 'components/JarvislyComponents/CpfCnpjInput';
import CardHeaderIcons from 'layouts/Pages/PageHeader/CardHeaderIcons';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const MODULE_ID = 'suppliers';

// Entity configuration - single source of truth for entityType and entityProfile
// - entityType: ['company'] = only legal entities (pessoa jurídica)
// - entityType: ['person'] = only individuals (pessoa física)
// - entityType: ['company', 'person'] = both (hybrid mode)
const ENTITY_CONFIG = {
  entityProfile: ['supplier'],
  entityType: ['person', 'company'],
};

// *******************************************************************************
// PRE CONFIG
// *******************************************************************************
const preConfigChanges = appId => {
  CONFIG.appId = appId;
};

// *******************************************************************************
// MODULE CONFIGURATION
// *******************************************************************************
const CONFIG = {
  version: 2,
  id: MODULE_ID,
  initialPage: 'init',
  initialParams: {},
  initialQuery: {},
  parametersModalComponent: SuppliersParametersModal,
  parametersSeed: seedSuppliersParameters,

  // ───────────────────────────────────────────────────────────────────────
  // MIGRATORY
  // ───────────────────────────────────────────────────────────────────────
  migratory: {
    title: 'supplier_migration',
    schema: [],
  },
};

// *******************************************************************************
// MODULE PAGES
// *******************************************************************************
const PAGES = {
  init: {
    // ───────────────────────────────────────────────────────────────────────
    // PAGE CORE
    // ───────────────────────────────────────────────────────────────────────
    layout: 'list', // 'page' | 'list' | 'document'
    moduleId: MODULE_ID,
    component: props => (
      <ListProvider {...props}>
        <SuppliersInit />
      </ListProvider>
    ),

    // ───────────────────────────────────────────────────────────────────────
    // TOUR
    // ───────────────────────────────────────────────────────────────────────
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: {
        br: null,
        us: null,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // API & DATA
    // ───────────────────────────────────────────────────────────────────────
    api: {
      url: API.ENTITIES.PAGINATION,
      params: null,
      query: null,

      // data transformation
      returnAsObject: false,

      // initial configuration
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    // ───────────────────────────────────────────────────────────────────────
    // DECORATORS
    // ───────────────────────────────────────────────────────────────────────
    decorators: {
      request: null,
      response: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // ROUTING & NAVIGATION
    // ───────────────────────────────────────────────────────────────────────
    routing: {
      endpoint: null,
      paramKey: null,
      query: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - PAGINATION CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    // Sort structure: array of { field, order, index }
    // - field: string - Field path (e.g., 'name', 'cnpj.value', '_metadata.audit.createdAt')
    // - order: 'asc' | 'desc' - Sort direction
    // - index: number - Sort priority (1 = highest, sorted first)
    // ───────────────────────────────────────────────────────────────────────
    pagination: {
      pagination: { page: 1, size: 10 },
      sort: [
        { field: '_metadata.audit.updatedAt', order: 'desc', index: 1 },
        // { field: 'name', order: 'asc', index: 1 },
      ],
      search: {},
      textSearch: '',
      filter: ENTITY_CONFIG,
      drawer: { folder: 'inbox' },
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - TABLE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    table: {
      actions: [
        'edit',

        // 'view',

        'archive',
        'remove',
      ],
      hiddenColumns: [
        'line',
        'cpfCnpj',
        'name',
        'fullName',
        // 'nameWithAvatar',
        'fullNameWithAvatar',
        'createdAt',
      ],
    },

    // ───────────────────────────────────────────────────────────────────────
    // DOCUMENT PROVIDER ONLY - PAGE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    settings: null,

    // ───────────────────────────────────────────────────────────────────────
    // HEADER CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    header: {
      visible: true,
      style: 'default', // 'default' | 'card'
      actions: ['filter', 'refresh', suppliersInitActionButton(), suppliersInitMenuButton()],

      navigation: {
        showTitle: true,
        showBreadcrumb: true,
      },
    },
  },

  document: {
    highSecurity: false, // Verify userId in URL params matches storedUser._id

    // ───────────────────────────────────────────────────────────────────────
    // PAGE CORE
    // ───────────────────────────────────────────────────────────────────────
    layout: 'document',
    moduleId: MODULE_ID,
    component: props => (
      <DocumentProvider {...props}>
        <SuppliersDocument />
      </DocumentProvider>
    ),

    // ───────────────────────────────────────────────────────────────────────
    // TOUR
    // ───────────────────────────────────────────────────────────────────────
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: {
        br: null,
        us: null,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // API & DATA
    // ───────────────────────────────────────────────────────────────────────
    api: {
      url: API.ENTITIES.URL,
      params: null,
      query: {
        // Filter uses ENTITY_CONFIG for consistency with init page
        filter: JSON.stringify(ENTITY_CONFIG),
      },

      // data transformation
      returnAsObject: true,

      // initial configuration
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    // ───────────────────────────────────────────────────────────────────────
    // DECORATORS
    // ───────────────────────────────────────────────────────────────────────
    decorators: {
      request: suppliersDocumentDecorateRequest,
      response: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // ROUTING & NAVIGATION
    // ───────────────────────────────────────────────────────────────────────
    routing: {
      endpoint: '/:_id',
      paramKey: '_id',
      query: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - PAGINATION CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    pagination: null,

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - TABLE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    table: null,

    // ───────────────────────────────────────────────────────────────────────
    // DOCUMENT PROVIDER ONLY - PAGE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    settings: {
      initialTab: 'record',
      initialValues: {},
      actions: [
        'editRefresh',
        suppliersDocumentActionButton(),
        suppliersDocumentMenuButton(),
        suppliersDocumentDropdownMenu(),
        'return',
      ],
      viewMode: 'auto',
    },

    // ───────────────────────────────────────────────────────────────────────
    // HEADER CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    header: {
      visible: true,
      style: 'card', // 'default' | 'card'
      //
      avatar: {
        visible: false,
        placeholder: 'logo',
      },
      //
      navigation: {
        showTitle: true,
        showBreadcrumb: true,
      },

      card: {
        title: props => <CardHeaderTitle {...props} placeholder="new_supplier" />,
        subtitle: props => <CardHeaderSubtitle {...props} />,
        column1: props => (
          <CardHeaderColumn {...props} items={[documentCodeItem, createdAtItem, updatedAtItem]} />
        ),
        column2: props => (
          <CardHeaderColumn {...props} items={[emailItem, phoneItem, locationItem]} />
        ),
        column3: props => (
          <CardHeaderColumn {...props} items={[cpfCnpjItem, genderSegmentItem, websiteItem]} />
        ),
        icons: (data, countryCode) => <CardHeaderIcons data={data} countryCode={countryCode} />,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // CUSTOM COMPONENTS
    // ───────────────────────────────────────────────────────────────────────
    // components: {},
  },
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default { CONFIG, PAGES, preConfigChanges };
